import "./worldMapHero.css";

/**
 * Decorative SVG world map hero:
 * - Stylised continent shapes in forest green on bone paper
 * - Latitude/longitude grid + equator
 * - 8 terracotta pins around the globe connected by a dashed travel route
 * - Pins fade + rise in with a staggered animation
 * - Dashed route slowly "flows" to hint at direction
 *
 * Uses equirectangular projection on a 1000x500 viewBox so pin coords match real lat/lng.
 */

const proj = (lat, lng) => [(lng + 180) * (1000 / 360), (90 - lat) * (500 / 180)];

const PINS = [
  { name: "New York", lat: 40.7, lng: -74.0 },
  { name: "London", lat: 51.5, lng: -0.1 },
  { name: "Rome", lat: 41.9, lng: 12.5 },
  { name: "Cairo", lat: 30.0, lng: 31.2 },
  { name: "Delhi", lat: 28.6, lng: 77.2 },
  { name: "Bangkok", lat: 13.7, lng: 100.5 },
  { name: "Tokyo", lat: 35.7, lng: 139.7 },
  { name: "Sydney", lat: -33.9, lng: 151.2 },
];

const CONTINENTS = [
  // North America
  "155,88 178,74 208,68 240,65 268,68 292,74 314,88 322,108 322,130 316,152 302,170 288,178 270,178 254,175 244,182 238,198 228,214 218,222 208,216 198,200 188,182 174,166 164,150 156,132 150,112",
  // Central America / Caribbean
  "232,192 248,196 262,204 258,212 248,208 240,204",
  // South America
  "268,224 286,220 306,224 322,236 328,258 330,286 322,314 312,342 300,362 290,364 278,354 268,338 264,318 264,292 266,266 268,244",
  // Greenland
  "355,52 380,48 402,52 412,66 410,80 395,88 375,90 358,82 352,68",
  // Iceland
  "440,92 452,90 458,96 452,102 442,100",
  // British Isles
  "462,108 472,104 478,112 476,124 468,124 462,116",
  // Europe
  "480,102 505,100 530,102 552,108 566,118 566,130 556,140 542,146 522,148 502,146 484,140 474,132 476,120",
  // Scandinavia
  "512,80 528,74 542,78 548,90 542,102 528,104 516,98 510,88",
  // Africa
  "492,168 522,162 550,166 572,176 588,196 597,222 600,252 594,282 578,308 558,326 538,336 520,332 506,318 496,296 490,268 488,238 490,208 490,186",
  // Middle East + Arabia
  "570,168 592,164 612,170 620,186 618,204 608,218 592,220 578,212 572,196",
  // Asia (main landmass)
  "570,92 610,82 655,78 705,80 755,84 795,90 830,100 858,116 872,138 866,158 850,176 826,186 798,190 762,188 724,182 686,178 650,180 620,174 594,162 578,144 570,120",
  // India subcontinent
  "690,180 712,178 730,186 736,204 728,224 714,236 700,232 690,214 686,198",
  // Southeast Asia / Malay peninsula
  "760,200 776,210 780,228 770,240 762,232 756,218",
  // Indonesia (islands as small ovals)
  "770,240 792,246 812,246 828,242 826,252 806,256 786,254",
  // Japan
  "882,130 892,140 898,156 892,168 884,158 880,144",
  // Philippines
  "826,206 836,210 838,222 828,224 822,214",
  // Australia
  "830,290 856,282 884,282 908,286 928,298 932,318 918,332 892,338 866,336 846,326 834,314 828,302",
  // Tasmania
  "870,346 882,344 884,352 872,354",
  // New Zealand
  "948,350 958,346 966,356 962,370 952,368 946,358",
  // Madagascar
  "610,286 618,294 620,308 612,318 604,310 604,296",
  // Antarctica ribbon (subtle)
  "0,470 1000,470 1000,500 0,500",
];

export default function WorldMapHero({ overlay = "#1E3329", overlayOpacity = 0.32, fit = "contain" }) {
  const routePoints = PINS.map((p) => {
    const [x, y] = proj(p.lat, p.lng);
    return `${x},${y}`;
  }).join(" ");

  return (
    <div className="worldmap-hero" aria-hidden="true">
      <svg
        viewBox="0 0 1000 500"
        preserveAspectRatio={fit === "cover" ? "xMidYMid slice" : "xMidYMid meet"}
        xmlns="http://www.w3.org/2000/svg"
        className="worldmap-hero__svg"
      >
        <defs>
          <radialGradient id="paper-glow" cx="50%" cy="45%" r="70%">
            <stop offset="0%" stopColor="#EFEBE1" />
            <stop offset="100%" stopColor="#DFD8C7" />
          </radialGradient>
          <filter id="paper-noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix values="0 0 0 0 0.12  0 0 0 0 0.12  0 0 0 0 0.12  0 0 0 0.14 0" />
          </filter>
        </defs>

        {/* Paper background */}
        <rect width="1000" height="500" fill="url(#paper-glow)" />
        <rect width="1000" height="500" filter="url(#paper-noise)" opacity="0.35" />

        {/* Latitude / longitude grid */}
        <g stroke="#1F1F1F" strokeWidth="0.5" opacity="0.18">
          {[50, 100, 150, 200, 300, 350, 400, 450].map((y) => (
            <line key={`h${y}`} x1="0" y1={y} x2="1000" y2={y} />
          ))}
          {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((x) => (
            <line key={`v${x}`} x1={x} y1="0" x2={x} y2="500" />
          ))}
        </g>

        {/* Equator */}
        <line x1="0" y1="250" x2="1000" y2="250" stroke="#1F1F1F" strokeWidth="1" strokeDasharray="4 6" opacity="0.35" />
        {/* Tropics */}
        <line x1="0" y1="185" x2="1000" y2="185" stroke="#1F1F1F" strokeWidth="0.5" strokeDasharray="2 6" opacity="0.22" />
        <line x1="0" y1="315" x2="1000" y2="315" stroke="#1F1F1F" strokeWidth="0.5" strokeDasharray="2 6" opacity="0.22" />

        {/* Continents */}
        <g fill="#2C4A3E" stroke="#1E3329" strokeWidth="0.6" strokeLinejoin="round">
          {CONTINENTS.map((pts, i) => (
            <polygon key={i} points={pts} />
          ))}
        </g>

        {/* Travel route */}
        <polyline
          points={routePoints}
          fill="none"
          stroke="#B85C47"
          strokeWidth="2.6"
          strokeDasharray="8 7"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="worldmap-hero__route"
        />

        {/* Pins */}
        {PINS.map((p, i) => {
          const [x, y] = proj(p.lat, p.lng);
          return (
            <g
              key={p.name}
              className="worldmap-hero__pin"
              style={{ animationDelay: `${i * 180 + 400}ms` }}
            >
              <circle cx={x} cy={y} r="14" fill="#B85C47" opacity="0.22" className="worldmap-hero__halo" />
              <circle cx={x} cy={y} r="6.5" fill="#B85C47" stroke="#F7F5F0" strokeWidth="2" />
              <circle cx={x} cy={y} r="1.6" fill="#F7F5F0" />
            </g>
          );
        })}

        {/* Compass rose */}
        <g transform="translate(60,60)" opacity="0.55" stroke="#1F1F1F" fill="none" strokeWidth="1">
          <circle r="18" />
          <line x1="0" y1="-22" x2="0" y2="22" />
          <line x1="-22" y1="0" x2="22" y2="0" />
          <polygon points="0,-22 3,-8 -3,-8" fill="#B85C47" stroke="#963F2B" />
        </g>
      </svg>

      <div
        className="worldmap-hero__overlay"
        style={{ background: overlay, opacity: overlayOpacity }}
      />
    </div>
  );
}
